"use client";

import { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Avatar,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Badge,
} from "@mui/material";
import {
  Search,
  Close,
  CheckCircle,
  Cancel,
  EventAvailable,
  Person,
  CalendarToday,
  Hotel,
  Star,
  Check,
  Add,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
// Add at the top of your file if not already there
import React from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonContent,
  IonText,
} from "@ionic/react";
import { formatCurrencyN } from "../../../common/utils_t";
import { getBarInventoryByBranch_hotel } from "../../../api/products/product";
import { baseUrl, config, requestID } from "../../../common/utils";
import axios from "axios";

const StyledRoomCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  cursor: "pointer",
  transition: "all 0.3s ease",
  border: `1px solid ${theme.palette.divider}`,
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[4],
    borderColor: theme.palette.primary.main,
  },
}));

const AvailabilityBadge = styled(Chip)(({ theme, status }) => ({
  fontWeight: 600,
  backgroundColor: status === 'available' 
    ? theme.palette.success.light 
    : theme.palette.error.light,
  color: status === 'available' 
    ? theme.palette.success.dark 
    : theme.palette.error.dark,
}));

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#fef2f2', borderRadius: 2 }}>
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>
            Something went wrong
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please try refreshing the page or contact support if the problem persists.
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

const AvailableRoomsToday = () => {
  const history = useHistory();
  const [allRooms, setAllRooms] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [filterBy, setFilterBy] = useState("all");
  const [sortBy, setSortBy] = useState("name");

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = () => {
    const today = new Date();
    return today.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Fetch ALL rooms (for statistics)
  const fetchAllRooms = async () => {
    try {
      const response = await getBarInventoryByBranch_hotel();
      // Filter only ROOMS category, exclude swimming pool and conference hall if needed
      const roomsOnly = response.data.filter(room => 
        room.category === 'ROOMS' 
        // && 
        // !room.name.includes('SWIMMING POOL') && 
        // !room.name.includes('CONFERENCE HALL')
      );
      return roomsOnly;
    } catch (error) {
      console.error("Error fetching all rooms:", error);
      throw error;
    }
  };

  // Fetch AVAILABLE rooms for today from back-end
  const fetchAvailableRoomsFromAPI = async () => {
    const today = getTodayDate();
    const data = {
      requestID: requestID(),
      business_id: 'bus_id_470fa0bc1c4ea4446e1be0ce31aa8018', // Replace with dynamic
      branch_id: 'bra_id_c91c3049f001472749851911045d31ff',   // Replace with dynamic
      date: today
    };
    
    try {
      const response = await axios.post(
        baseUrl() + 'orderhistorys/fetchAvailableRooms', 
        data,
        config
      );
      console.log(response);
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching available rooms:", error);
      throw error;
    }
  };

  // Main fetch function with error handling
  const fetchRoomsData = async () => {
    setLoading(true);
    try {
      // Fetch both all rooms and available rooms in parallel
      const [allRoomsData, availableRoomsData] = await Promise.all([
        fetchAllRooms(),
        fetchAvailableRoomsFromAPI()
      ]);

      // Create a map of available room IDs for quick lookup
      const availableRoomIds = new Set(
        availableRoomsData.map(room => room.product_id)
      );

      // Combine data: mark each room with availability status
      const roomsWithStatus = allRoomsData.map(room => ({
        ...room,
        available: availableRoomIds.has(room.product_id),
        // Add capacity based on room name if not in data
        capacity: getRoomCapacity(room.name)
      }));

      setAllRooms(roomsWithStatus);
      setAvailableRooms(availableRoomsData);
      
    } catch (error) {
      console.error("Error fetching rooms data:", error);
      setSnackbarMessage("Failed to load rooms data. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      // Set empty arrays to prevent further errors
      setAllRooms([]);
      setAvailableRooms([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper to estimate room capacity from room name
  const getRoomCapacity = (roomName) => {
    if (!roomName) return 2;
    if (roomName.includes('VIP') || roomName.includes('SUITES')) return 4;
    if (roomName.includes('DELUX') || roomName.includes('LUXURY')) return 3;
    if (roomName.includes('EXECUTIVE')) return 2;
    return 2; // default
  };

  // Filter and sort rooms
  useEffect(() => {
    let result = [...allRooms];
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(room =>
        room.name && room.name.toLowerCase().includes(query)
      );
    }
    
    // Apply availability filter
    if (filterBy === 'available') {
      result = result.filter(room => room.available);
    } else if (filterBy === 'occupied') {
      result = result.filter(room => !room.available);
    }
    
    // Apply sorting
    result.sort((a, b) => {
      switch (sortBy) {
        case 'price':
          return (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0);
        case 'capacity':
          return (a.capacity || 0) - (b.capacity || 0);
        case 'name':
        default:
          return (a.name || '').localeCompare(b.name || '');
      }
    });
    
    setFilteredRooms(result);
  }, [allRooms, searchQuery, filterBy, sortBy]);

  useEffect(() => {
    fetchRoomsData();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchRoomsData, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const handleBookRoom = (room) => {
    if (!room || !room.available) return;
    setSelectedRoom(room);
    setShowBookingDialog(true);
  };

  const handleGoToBooking = () => {
    history.push('/folder/hotel_booking/hotel_booking');
  };

    const handleQuickBook = () => {
    history.push('/folder/hotel_booking/hotel_booking');
  };


  // const handleQuickBook = () => {
  //   if (selectedRoom) {
      
  //     history.push({
  //       pathname: '/folder/hotel_booking/hotel_booking'
  //     });
       
  //   }
  // };

  // FIXED: Correct function name (lowercase 'h')
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Calculate statistics with safe defaults
  const stats = useMemo(() => {
    const totalRooms = allRooms.length || 0;
    const availableCount = allRooms.filter(r => r && r.available).length || 0;
    const occupiedCount = totalRooms - availableCount;
    const occupancyRate = totalRooms > 0 ? ((occupiedCount / totalRooms) * 100).toFixed(1) : 0;
    
    return { 
      totalRooms, 
      availableRooms: availableCount, 
      occupiedRooms: occupiedCount, 
      occupancyRate 
    };
  }, [allRooms]);

  const getAvatarSrc = (src) => {
    return src && src.trim() !== "" ? src : "/placeholder.svg";
  };

  return (
    <ErrorBoundary>
      <IonPage className="pageDown">
        <IonHeader>
          <IonToolbar>
            <IonText style={{ padding: '0 16px' }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#667eea' }}>
                Today's Room Availability
              </Typography>
            </IonText>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <Box sx={{ p: 2, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
            {/* Header Section */}
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'white', borderRadius: 2, boxShadow: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#2d3748' }}>
                    Room Availability Dashboard
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#667eea', display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <CalendarToday fontSize="small" />
                    {formatDisplayDate()}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Hotel />}
                  onClick={handleGoToBooking}
                  sx={{
                    bgcolor: '#667eea',
                    color: 'white',
                    '&:hover': { bgcolor: '#5a6fd8' },
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1
                  }}
                >
                  Go to Full Booking
                </Button>
              </Box>

              {/* Statistics Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f0f9ff', borderRadius: 2 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#0369a1' }}>
                      {stats.totalRooms}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Rooms
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f0fdf4', borderRadius: 2 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#15803d' }}>
                      {stats.availableRooms}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Available Now
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fef2f2', borderRadius: 2 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#dc2626' }}>
                      {stats.occupiedRooms}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Occupied
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fffbeb', borderRadius: 2 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#d97706' }}>
                      {stats.occupancyRate}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Occupancy Rate
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Filters and Search */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <TextField
                  placeholder="Search rooms by name..."
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flexGrow: 1, minWidth: 200 }}
                />
                
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label="All Rooms"
                    onClick={() => setFilterBy('all')}
                    color={filterBy === 'all' ? 'primary' : 'default'}
                    variant={filterBy === 'all' ? 'filled' : 'outlined'}
                  />
                  <Chip
                    label={`Available (${stats.availableRooms})`}
                    onClick={() => setFilterBy('available')}
                    color={filterBy === 'available' ? 'success' : 'default'}
                    variant={filterBy === 'available' ? 'filled' : 'outlined'}
                    icon={<CheckCircle />}
                  />
                  <Chip
                    label={`Occupied (${stats.occupiedRooms})`}
                    onClick={() => setFilterBy('occupied')}
                    color={filterBy === 'occupied' ? 'error' : 'default'}
                    variant={filterBy === 'occupied' ? 'filled' : 'outlined'}
                    icon={<Cancel />}
                  />
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    label="Sort by: Name"
                    onClick={() => setSortBy('name')}
                    color={sortBy === 'name' ? 'primary' : 'default'}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label="Sort by: Price"
                    onClick={() => setSortBy('price')}
                    color={sortBy === 'price' ? 'primary' : 'default'}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label="Sort by: Capacity"
                    onClick={() => setSortBy('capacity')}
                    color={sortBy === 'capacity' ? 'primary' : 'default'}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>
            </Paper>

            {/* Rooms Grid */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading room availability...</Typography>
              </Box>
            ) : filteredRooms.length > 0 ? (
              <>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#4a5568' }}>
                  Showing {filteredRooms.length} rooms ({filterBy === 'all' ? `${stats.availableRooms} available, ${stats.occupiedRooms} occupied` : 
                    filterBy === 'available' ? 'available rooms only' : 'occupied rooms only'})
                </Typography>
                
                <Grid container spacing={3}>
                  {filteredRooms.map((room) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={room.product_id}>
                      <StyledRoomCard
                        onClick={() => handleBookRoom(room)}
                        sx={{ 
                          opacity: room.available ? 1 : 0.8,
                          cursor: room.available ? 'pointer' : 'not-allowed'
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                          {/* Room Image */}
                          <Box sx={{ position: 'relative', mb: 2 }}>
                            <Avatar
                              src={getAvatarSrc(room.img_src)}
                              alt={room.name}
                              variant="rounded"
                              sx={{ 
                                width: '100%', 
                                height: 150, 
                                objectFit: 'cover',
                                filter: room.available ? 'none' : 'grayscale(50%)'
                              }}
                            />
                            <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                              <AvailabilityBadge
                                label={room.available ? "AVAILABLE" : "OCCUPIED"}
                                status={room.available ? 'available' : 'occupied'}
                                size="small"
                              />
                            </Box>
                            {!room.available && (
                              <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                bgcolor: 'rgba(0,0,0,0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                                  OCCUPIED
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          {/* Room Info */}
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                              {room.name || 'Unnamed Room'}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Person fontSize="small" color="action" />
                              <Typography variant="body2">
                                Capacity: {room.capacity || 2} people
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Star fontSize="small" color="warning" />
                              <Typography variant="body2">
                                {room.category || 'Standard Room'}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Price and Action */}
                          <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #e0e0e0' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Price per night
                                </Typography>
                                <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                                  {formatCurrencyN(room.price || 0)}
                                </Typography>
                              </Box>
                            </Box>
                            
                            <Button
                              fullWidth
                              variant={room.available ? "contained" : "outlined"}
                              color={room.available ? "primary" : "error"}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBookRoom(room);
                              }}
                              disabled={!room.available}
                              startIcon={room.available ? <Add /> : <Cancel />}
                              sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                              }}
                            >
                              {room.available ? 'Book Now' : 'Currently Occupied'}
                            </Button>
                          </Box>
                        </Box>
                      </StyledRoomCard>
                    </Grid>
                  ))}
                </Grid>
              </>
            ) : (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <EventAvailable sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No rooms found matching your criteria
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Try adjusting your search or filters
                </Typography>
              </Paper>
            )}
          </Box>

          {/* Booking Dialog */}
          <Dialog open={showBookingDialog} onClose={() => setShowBookingDialog(false)}>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Hotel />
                Book {selectedRoom?.name}
              </Box>
              <IconButton
                sx={{ position: 'absolute', right: 8, top: 8 }}
                onClick={() => setShowBookingDialog(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {selectedRoom && (
                <>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Avatar
                      src={getAvatarSrc(selectedRoom.img_src)}
                      alt={selectedRoom.name}
                      sx={{ width: 100, height: 100 }}
                    />
                    <Box>
                      <Typography variant="h6">{selectedRoom.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedRoom.category || 'Standard Room'}
                      </Typography>
                      <Typography variant="h5" color="primary" sx={{ mt: 1 }}>
                        {formatCurrencyN(selectedRoom.price || 0)}/night
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Alert severity="success" sx={{ mb: 2 }}>
                    This room is available for booking today ({getTodayDate()})
                  </Alert>
                  
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Room Details:
                    </Typography>
                    <Typography variant="body2">
                      • Capacity: {selectedRoom.capacity || 2} people
                    </Typography>
                    <Typography variant="body2">
                      • Category: {selectedRoom.category || 'Standard'}
                    </Typography>
                    <Typography variant="body2">
                      • Status: <Badge color="success" variant="dot" /> Available
                    </Typography>
                  </Box>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowBookingDialog(false)}>Cancel</Button>
              <Button
                variant="contained"
onClick={() => {
  setShowBookingDialog(false);
  handleQuickBook();
}}                startIcon={<Check />}
              >
                Proceed to Booking
              </Button>
            </DialogActions>
          </Dialog>

          {/* FIXED: Using the correct function name handleSnackbarClose */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert severity={snackbarSeverity} onClose={handleSnackbarClose}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </IonContent>
      </IonPage>
    </ErrorBoundary>
  );
};

export default AvailableRoomsToday;